import { CalculationStrategies, ProjectSample } from '../../@types/samples/types';
import { UpdateSamplePayload } from '../../@types/samples/types';
import { Well } from '../../@types/wells/types';
import { AVERAGE_ROW_UID, EditableField, SummaryRow } from './types';

export const ANALYSIS_LABELS: Record<CalculationStrategies, string> = {
  [CalculationStrategies.LIAO]: 'Liao',
  [CalculationStrategies.LAMBDA]: 'Lambda',
  [CalculationStrategies.WATER_OIL_RELATIVE_PERMEABIILITY]: 'Water-Oil',
  [CalculationStrategies.GAS_OIL_RELATIVE_PERMEABIILITY]: 'Gas-Oil',
};

/** Stable label for a sample's set of performed analyses (used for grouping). */
export const analysisSignature = (analyses: CalculationStrategies[]): string => {
  if (!analyses.length) return 'No analysis';
  return [...analyses]
    .map(strategy => ANALYSIS_LABELS[strategy] ?? strategy)
    .sort((a, b) => a.localeCompare(b))
    .join(' + ');
};

/** Editable fields that live under `input_sample_values` in the update payload. */
export const INPUT_VALUE_FIELDS: EditableField[] = [
  'porosity',
  'permeability',
  'ko',
  'krw',
  'qv',
  'salinity',
  'swi',
];

/** All numeric editable fields (everything except the textual sample_code). */
export const NUMERIC_EDITABLE_FIELDS: EditableField[] = [
  'depth',
  ...INPUT_VALUE_FIELDS,
];

export const EDITABLE_FIELDS: EditableField[] = [
  'sample_code',
  ...NUMERIC_EDITABLE_FIELDS,
];

/** Columns averaged in the synthetic footer row. */
export const AVERAGEABLE_FIELDS: (keyof SummaryRow)[] = [
  'depth',
  'porosity',
  'permeability',
  'fzi',
  'ko',
  'krw',
  'qv',
  'swi',
  'salinity',
  'kro_absolute',
  'krw_absolute',
  'krg_absolute',
  'sor_w',
  'no_w',
  'nw',
  'true_sor_w',
  'true_no_w',
  'true_nw',
  'true_krw',
  'true_kro_w',
  'sor_g',
  'no_g',
  'ng',
  'true_sor_g',
  'true_no_g',
  'true_ng',
  'true_krg',
  'true_kro_g',
];

/** Build a flat row from a sample, resolving the well name from the well list. */
export const toSummaryRow = (
  sample: ProjectSample,
  wellNameByUid: Record<string, string>,
): SummaryRow => {
  const v = sample.input_sample_values;
  const waterOil = sample.k_rel_values?.water_oil ?? null;
  const gasOil = sample.k_rel_values?.gas_oil ?? null;

  return {
    uid: sample.uid,
    well_uid: sample.well_uid,
    wellName: wellNameByUid[sample.well_uid] ?? '—',
    analyses: sample.performed_analyses ?? [],
    depth_unit: sample.depth_unit ?? 'ft',

    sample_code: sample.sample_code,
    depth: sample.depth ?? null,
    porosity: v?.porosity ?? null,
    permeability: v?.permeability ?? null,
    ko: v?.ko ?? null,
    krw: v?.krw ?? null,
    qv: v?.qv ?? null,
    swi: v?.swi ?? null,
    salinity: v?.salinity ?? null,

    fzi: v?.fzi ?? null,
    kro_absolute: v?.kro_absolute ?? null,
    krw_absolute: v?.krw_absolute ?? null,
    krg_absolute: v?.krg_absolute ?? null,
    sor_w: waterOil?.sor_w ?? null,
    no_w: waterOil?.no_w ?? null,
    nw: waterOil?.nw ?? null,
    true_sor_w: waterOil?.true_sor_w ?? null,
    true_no_w: waterOil?.true_no_w ?? null,
    true_nw: waterOil?.true_nw ?? null,
    true_krw: waterOil?.true_krw ?? null,
    true_kro_w: waterOil?.true_kro_w ?? null,
    sor_g: gasOil?.sor_g ?? null,
    no_g: gasOil?.no_g ?? null,
    ng: gasOil?.ng ?? null,
    true_sor_g: gasOil?.true_sor_g ?? null,
    true_no_g: gasOil?.true_no_g ?? null,
    true_ng: gasOil?.true_ng ?? null,
    true_krg: gasOil?.true_krg ?? null,
    true_kro_g: gasOil?.true_kro_g ?? null,
  };
};

export const buildWellNameMap = (wells: Well[]): Record<string, string> =>
  wells.reduce<Record<string, string>>((acc, well) => {
    acc[well.uid] = well.well_name;
    return acc;
  }, {});

/** Format a numeric value for display; empty cells render an em dash. */
export const formatNumber = (
  value: number | null | undefined,
  digits = 4,
): string => {
  if (value == null || Number.isNaN(value)) return '—';
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(digits);
};

/**
 * Validate a single editable field. Returns an error message or null. Mirrors
 * the backend Pydantic validators in `InputSampleValues` / `Sample`.
 */
export const validateField = (
  field: EditableField,
  raw: string,
): string | null => {
  const trimmed = raw.trim();

  if (field === 'sample_code') {
    if (trimmed.length < 1) return 'Sample code is required';
    if (trimmed.length > 50) return 'Max 50 characters';
    return null;
  }

  if (trimmed === '') return 'Required';
  const value = Number(trimmed);
  if (Number.isNaN(value)) return 'Must be a number';

  switch (field) {
    case 'depth':
      if (!Number.isFinite(value)) return 'Must be finite';
      if (value <= 0) return 'Must be greater than 0';
      return null;
    case 'porosity':
      if (value < 0 || value >= 1) return 'Must be between 0 and 1';
      return null;
    case 'swi':
    case 'krw':
      if (value < 0 || value > 1) return 'Must be between 0 and 1';
      return null;
    case 'ko':
      if (value <= 0) return 'Must be greater than 0';
      return null;
    case 'permeability':
    case 'salinity':
    case 'qv':
      if (value < 0) return 'Must be 0 or greater';
      return null;
    default:
      return null;
  }
};

const numbersDiffer = (a: number | null, b: number | null): boolean => {
  if (a == null && b == null) return false;
  if (a == null || b == null) return true;
  return Math.abs(a - b) > 1e-9;
};

export interface BuildPayloadResult {
  payload: UpdateSamplePayload | null;
  errors: Partial<Record<EditableField, string>>;
}

/**
 * Build a minimal `UpdateSamplePayload` containing only the changed editable
 * fields. Validates each edit; if any fail, returns `payload: null` with the
 * collected errors. Returns `payload: null` with no errors when nothing changed.
 */
export const buildUpdatePayload = (
  original: ProjectSample,
  edits: Partial<Record<EditableField, string>>,
): BuildPayloadResult => {
  const errors: Partial<Record<EditableField, string>> = {};

  (Object.keys(edits) as EditableField[]).forEach(field => {
    const error = validateField(field, edits[field] ?? '');
    if (error) errors[field] = error;
  });

  if (Object.keys(errors).length > 0) {
    return { payload: null, errors };
  }

  const payload: UpdateSamplePayload = { sample_uid: original.uid };
  const inputValues: Partial<Record<EditableField, number>> = {};
  let changed = false;

  (Object.keys(edits) as EditableField[]).forEach(field => {
    const raw = (edits[field] ?? '').trim();

    if (field === 'sample_code') {
      if (raw !== original.sample_code) {
        payload.sample_code = raw;
        changed = true;
      }
      return;
    }

    const value = Number(raw);

    if (field === 'depth') {
      if (numbersDiffer(value, original.depth ?? null)) {
        payload.depth = value;
        changed = true;
      }
      return;
    }

    // input_sample_values fields
    const current = original.input_sample_values?.[field] ?? null;
    if (numbersDiffer(value, current)) {
      inputValues[field] = value;
      changed = true;
    }
  });

  if (Object.keys(inputValues).length > 0) {
    payload.input_sample_values = inputValues;
  }

  return { payload: changed ? payload : null, errors };
};

/** Compute the synthetic average row for a set of summary rows. */
export const computeAverageRow = (rows: SummaryRow[]): SummaryRow => {
  const average: SummaryRow = {
    uid: AVERAGE_ROW_UID,
    well_uid: '',
    wellName: 'Average',
    analyses: [],
    depth_unit: rows[0]?.depth_unit ?? 'ft',
    sample_code: 'Average',
    depth: null,
    porosity: null,
    permeability: null,
    ko: null,
    krw: null,
    qv: null,
    swi: null,
    salinity: null,
    fzi: null,
    kro_absolute: null,
    krw_absolute: null,
    krg_absolute: null,
    sor_w: null,
    no_w: null,
    nw: null,
    true_sor_w: null,
    true_no_w: null,
    true_nw: null,
    true_krw: null,
    true_kro_w: null,
    sor_g: null,
    no_g: null,
    ng: null,
    true_sor_g: null,
    true_no_g: null,
    true_ng: null,
    true_krg: null,
    true_kro_g: null,
    isAverage: true,
  };

  AVERAGEABLE_FIELDS.forEach(field => {
    const values = rows
      .map(row => row[field])
      .filter((value): value is number => typeof value === 'number' && !Number.isNaN(value));
    if (values.length > 0) {
      const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
      (average[field] as number) = mean;
    }
  });

  return average;
};

/** Sort summary rows by a column key and direction. Strings sort case-insensitively. */
export const sortRows = (
  rows: SummaryRow[],
  key: keyof SummaryRow,
  direction: 'asc' | 'desc',
): SummaryRow[] => {
  const factor = direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = a[key];
    const bv = b[key];
    if (av == null && bv == null) return 0;
    if (av == null) return 1; // nulls last
    if (bv == null) return -1;
    if (typeof av === 'number' && typeof bv === 'number') {
      return (av - bv) * factor;
    }
    return String(av).localeCompare(String(bv)) * factor;
  });
};
